import { Injectable, Optional }         from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams
}                                       from "@angular/common/http";
import 'rxjs/add/operator/toPromise';

import { EventsService }                from './events.service';
import { AppConfig }                    from '../../app.config';
import {
    EXPIRED_AUTH_TOKEN,
    SLUG_ABOUT_US,
    SLUG_BECOME_A_TUTOR
}                                       from '../../app.constants';
import { isDef, objectKeysToSnakeCase } from '../../utils';

@Injectable()
export class NetworkService {

  private apiURL;
  private headers;

  constructor(
      @Optional() config: AppConfig,
      private eventBus: EventsService,
      private http: HttpClient
    ) {

    if (config) {
      this.apiURL = config.apiURL;
    }
  }

  initSession (authToken: string) {
    this.headers = new HttpHeaders().set('Authorization', `Bearer ${authToken}`)
  }

  removeSession () {
    this.headers = null;
  }

  fetchCategories () {
      return this.get(this.apiURL + '/themes');
  }

  fetchPopular() {
      return this.get(this.apiURL + '/products/popular');
  }

  fetchRecommended() {
      return this.get(this.apiURL + '/products/recommended');
  }

  fetchProductInfo(id) {
      return this.get(this.apiURL + `/products/${id}`);
  }

  getProduct(link) {
    return this.get(link, {}, true);
  }

  removeFromBasket (product) {
    return Promise.resolve(true);
  }

  searchForProduct(params) {
      const requestHeaders = new HttpHeaders({'Content-Type': 'application/json'});
      let requestParams = new HttpParams();

      if (params.text) {
          requestParams = requestParams.set('text', params.text);
      } else if (params.typeId) {
          requestParams = requestParams.set('typeId', String(params.typeId));
      } else if (params.themeId) {
          requestParams = requestParams.set('themeId', String(params.themeId));
      }

      const options = { headers: requestHeaders, params: requestParams};

      return this.get(this.apiURL + '/search', options);

  }

  fetchContactInfo(slug: string) {
      return this.get(this.apiURL + '/pages/' + slug);
  }

  addProductToCart (id: number) {
    return this.post(this.apiURL + '/cart/product', {id});
  }

  addToWishList (id) {
    return this.post(this.apiURL + '/users/wishlist', { id });
  }

  clearCart () {
    return this.post(this.apiURL + '/cart/clear', {});
  }

  createOrder () {
    return this.post(this.apiURL + '/order', {});
  }

  createPayment(uuid, body) {
    return this.post(this.apiURL + `/orders/${uuid}/payment`, body);
  }

  getClientToken () {
    return this.get(this.apiURL + `/payments/token`)
  }

  fetchOrder (token) {
    return this.get(this.apiURL + `/orders/${ token }`);
  }

  removeFromWishList (id) {
    return this.delete(`${this.apiURL}/users/wishlist/${id}`);
  }

  removeProductFromCart (id) {
    return this.delete(`${this.apiURL}/cart/product/${id}`);
  }

  resetPassword(body) {
      return this.patch(this.apiURL + '/password', body);
  }

  sendEmailForResetPassword(body) {
      return this.post(this.apiURL + '/password', body);
  }

  signUpUser(body) {
      return this.post(this.apiURL + '/users', body);
  }

  logIn (reqData) {
    return this.post(this.apiURL + '/login', reqData);
  }

  logOut () {
    return this.get(this.apiURL + '/logout');
  }

  fetchUserData () {
    return this.get(this.apiURL + '/users');
  }

  updateUserInfo(body) {
    return this.patch(this.apiURL + '/users', body)
  }

  fetchWishList () {
    return this.get(this.apiURL + '/users/wishlist');
  }

  fetchUserProductList() {
    return this.get(this.apiURL + '/users/productlist');
  }

  fetchCart () {
    return this.get(this.apiURL + '/cart');
  }

  ///////////////////////////

  private get (url: string, opt?: any, allData?: boolean): Promise<any> {
    const options = opt || {};

    if (isDef(this.headers)) {
      options.headers = this.headers;
    }

    return this.http
      .get(url, options)
      .toPromise()
      .then(response => {
        return this.extractData(response, allData);
      })
      .catch(err => {
        return this.handleError(err);
      });
  }

  private post (url: string, body: object, opt?: any): Promise<any> {
    const options = opt || {}

    if (isDef(this.headers)) {
      options.headers = this.headers;
    }

    return this.http
      .post(url, objectKeysToSnakeCase(body), options)
      .toPromise()
      .then(response => {
        return this.extractData(response);
      })
      .catch(err => {
        return this.handleError(err);
      });
  }

  private patch(url: string, body: object, opt?: any): Promise<any> {
      const options = opt || {};

        if (isDef(this.headers)) {
          options.headers = this.headers;
        }

        return this.http
          .patch(url, objectKeysToSnakeCase(body), options)
          .toPromise()
          .then(response => {
              return this.extractData(response)
          })
          .catch(err => {
              return this.handleError(err);
          })
    }

  private put (url: string, body: object, opt?: any): Promise<any> {
    const options = opt || {}

    if (isDef(this.headers)) {
      options.headers = this.headers;
    }

    return this.http
      .put(url, objectKeysToSnakeCase(body), options)
      .toPromise()
      .then(response => {
        return this.extractData(response);
      })
      .catch(err => {
        return this.handleError(err);
      })

  }

  private delete (url: string, opt?: any): Promise<any> {
    const options = opt || {}

    if (isDef(this.headers)) {
      options.headers = this.headers;
    }

    return this.http
      .delete(url, options)
      .toPromise()
      .then(response => {
        return this.extractData(response);
      })
      .catch(err => {
        return this.handleError(err);
      });
  }

  private extractData (res, allData?): Promise<any> {
      const isAllData = allData || false;
      if (res === null) {
          return Promise.resolve();
      }
      return isAllData ? Promise.resolve(res) : Promise.resolve(res.data);
  }

  private handleError (error: any): Promise<any> {
    if (error.status && error.status === 401) {
      this.eventBus.trigger(EXPIRED_AUTH_TOKEN);
    }

    return Promise.reject(error);
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/(IGH Projects)/jobs/EVFS PROD_FRONTEND granima.de/workspace/frontend/src/app/core/services/network.service.ts